"use client";

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useSyncExternalStore,
  ReactNode,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import { IntlProvider as ReactIntlProvider } from "react-intl";
import { messages, defaultLocale, type Locale } from "@/lang";
import { localeFromPathname, localePath } from "@/lib/locale-routing";

type LocaleContextType = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
};

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

const LOCALE_STORAGE_KEY = "flots-locale";
const LOCALE_CHANGE_EVENT = "flots-locale-change";

function getStoredLocale(): Locale {
  const savedLocale = localStorage.getItem(LOCALE_STORAGE_KEY);
  return savedLocale === "fr" || savedLocale === "en"
    ? savedLocale
    : defaultLocale;
}

function subscribeToLocale(onStoreChange: () => void) {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === LOCALE_STORAGE_KEY) onStoreChange();
  };

  window.addEventListener("storage", handleStorage);
  window.addEventListener(LOCALE_CHANGE_EVENT, onStoreChange);

  return () => {
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener(LOCALE_CHANGE_EVENT, onStoreChange);
  };
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within an IntlProvider");
  }
  return context;
}

type IntlProviderProps = {
  children: ReactNode;
  // Locale implied by the URL of the FIRST server render (see proxy.ts +
  // app/layout.tsx). Only used as the server snapshot for pages without a
  // per-language URL — the root layout renders once and never re-renders on
  // client-side navigation, so this prop goes stale as soon as the user
  // navigates. Never treat it as "the current URL's locale": that exact
  // mistake made every client-side navigation re-impose the language of the
  // first page load (FR sticking around on /en URLs).
  initialLocale?: Locale;
};

export function IntlProvider({ children, initialLocale }: IntlProviderProps) {
  const pathname = usePathname();
  const router = useRouter();

  const storedLocale = useSyncExternalStore(
    subscribeToLocale,
    getStoredLocale,
    () => initialLocale ?? defaultLocale,
  );

  // On a dual-URL page (home, resources, legal pages) the URL — read live
  // from usePathname(), correct on both the server render and every
  // client-side navigation — is authoritative for language. Everywhere else
  // (e.g. /team, single URL for both languages) the saved preference drives.
  const urlLocale = localeFromPathname(pathname);
  const locale = urlLocale ?? storedLocale;

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Keep the saved preference aligned with the URL, so leaving a dual-URL
  // page for a preference-driven one (e.g. /en → /team) stays in the same
  // language instead of falling back to a stale stored value.
  useEffect(() => {
    if (!urlLocale) return;
    if (getStoredLocale() === urlLocale) return;
    localStorage.setItem(LOCALE_STORAGE_KEY, urlLocale);
    window.dispatchEvent(new Event(LOCALE_CHANGE_EVENT));
  }, [urlLocale]);

  const setLocale = useCallback((newLocale: Locale) => {
    localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    window.dispatchEvent(new Event(LOCALE_CHANGE_EVENT));

    // On a dual-URL page, switching language IS a navigation to the twin URL
    // (otherwise the URL would claim one language and the text another).
    const target = localePath(pathname, newLocale);
    if (target && target !== pathname) router.push(target);
  }, [pathname, router]);

  const toggleLocale = useCallback(() => {
    const newLocale = locale === "fr" ? "en" : "fr";
    setLocale(newLocale);
  }, [locale, setLocale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, toggleLocale }}>
      <ReactIntlProvider
        locale={locale}
        messages={messages[locale]}
        defaultLocale={defaultLocale}
        onError={(err) => {
          // Suppress missing translation warnings in development
          if (err.code === "MISSING_TRANSLATION") {
            console.warn("Missing translation:", err.message);
            return;
          }
          throw err;
        }}
      >
        {children}
      </ReactIntlProvider>
    </LocaleContext.Provider>
  );
}
